import {FC, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Icon, Modal} from "semantic-ui-react";
import {LoadingMessage, ErrorMessage} from "@/components/SystemMessages";
import {
    DataStatus,
    IColumnMetadata,
    IDataTableData
} from "@/features/application/api/applicationModels";
import {DataType} from "@/util/dataFormatUtil";
import dataTableUtil from "@/components/DataTable/dataTableUtils";
import {DataTable} from "@/components/DataTable";
import {AppState} from "@/state/rootReducer";
import {IStaffCoverageResponse} from "../api/coachInsightsModels";
import coachInsightsAPI from "../api/coachInsightsAPI";
import {ISODateString} from "@/util/dateUtil";
import {DateTime} from "luxon";

function formatPunchTime(clockIn: string, clockOut: string): string {
    const clockInasDateTime = DateTime.fromISO(clockIn);
    const clockOutAsDateTime = DateTime.fromISO(clockOut);
    const suffix =
        clockOutAsDateTime.diff(clockInasDateTime, "hours").hours > 16
            ? " (likely invalid punch)"
            : "";
    return `${clockInasDateTime.toFormat("h:mm a")} - ${clockOutAsDateTime.toFormat(
        "h:mm a"
    )}${suffix}`;
}

interface IProps {
    open: boolean;
    onClose: () => void;
    locationPk: number;
    businessDate: ISODateString;
}

function buildColumns(): IColumnMetadata[] {
    return [
        {
            key: "name",
            label: "Name",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "jobRole",
            label: "Job Role",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "punchTime",
            label: "Punch Time",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "hours",
            label: "Time Worked During Peak",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        },
        {
            key: "tenure",
            label: "Tenure",
            dataType: DataType.None,
            decimalPlaces: 0,
            hasFilter: false,
            isToggleable: false,
            isToggledToVisible: true,
            tooltipText: "",
            unit: ""
        }
    ];
}

const StaffCoverageModal: FC<IProps> = ({
    open,
    onClose,
    locationPk,
    businessDate
}) => {
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.Loading);
    const [data, setData] = useState<IStaffCoverageResponse | null>(null);
    const screenWidth = useSelector(
        (state: AppState) => state.application.screenWidth
    );
    const columns = useMemo(() => buildColumns(), []);

    useEffect(() => {
        if (!open) return;

        const abortController = new AbortController();
        setDataStatus(DataStatus.Loading);

        const fetchData = async () => {
            try {
                const response = await coachInsightsAPI.getStaffCoverage(
                    locationPk,
                    businessDate,
                    abortController.signal
                );
                setData(response);
                setDataStatus(DataStatus.Loaded);
            } catch (error: any) {
                if (error?.name !== "AbortError") {
                    setDataStatus(DataStatus.Failed);
                }
            }
        };

        fetchData();
        return () => abortController.abort();
    }, [open, locationPk, businessDate]);

    const tableData = useMemo<IDataTableData[]>(() => {
        if (!data) return [];
        return dataTableUtil.mapDataTableData(
            data.staffMembers.map((m) => ({
                name: m.name,
                jobRole: m.jobRole,
                punchTime: formatPunchTime(m.clockIn, m.clockOut),
                hours: `${m.hours.toFixed(1)} hrs`,
                tenure: m.tenure
            }))
        );
    }, [data]);

    return (
        <Modal
            className="insights-modal"
            open={open}
            onClose={onClose}
            closeIcon
            closeOnDimmerClick={true}
            size="large"
        >
            <Modal.Header>
                <div className="modal-header-content">
                    <div className="modal-header-left">
                        <Icon name="users" />
                        <span className="modal-title">Staff Coverage</span>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Content scrolling>
                {dataStatus === DataStatus.Loading && <LoadingMessage />}
                {dataStatus === DataStatus.Failed && (
                    <ErrorMessage text="Failed to load staff coverage." />
                )}
                {dataStatus === DataStatus.Loaded && data && (
                    <div className="staff-coverage-table">
                        <DataTable
                            staticColumns={columns}
                            data={tableData}
                            sortColumnKey="name"
                            itemsPerPage={16}
                            customColumns={{}}
                            screenWidth={screenWidth}
                        />
                    </div>
                )}
            </Modal.Content>
        </Modal>
    );
};

export default StaffCoverageModal;
